import vClickOutside from "v-click-outside";
import algoliasearch from "algoliasearch/lite";
import _debounce from "lodash/debounce";
import { mapGetters } from "vuex";
import {RouterPath} from "../../../services/utils";

const SEARCH_DEBOUNCE_DURATION = 500;

export default {
  props: ["show"],

  directives: {
    clickOutside: vClickOutside.directive,
  },

  data() {
    return {
      vcoConfig: {
        handler: this.closeSearch,
        isActive: this.show,
        middleware(event) {
          const type = typeof event.target.className;
          let value = null;

          if (event.target.className && type !== "object") {
            value = event.target.className.includes("search-toggle");
          }

          return !value;
        },
      },
      algoliaClient: algoliasearch(this.$env.algolia().appId, this.$env.algolia().token, {
        timeouts: {
          connect: this.$utils.constants.ALGOLIA_CONNECT_TIMEOUT,
          read: this.$utils.constants.ALGOLIA_READ_TIMEOUT,
        },
      }),
      query: "",
      isLoading: {
        brands: false,
        products: false,
      },
      brandSuggestions: null,
      brandSearchIndex: null,
      productSuggestions: null,
      productSearchIndex: null,
      urlFormatter: this.$utils.urlSafeString,
      suggestionIndex: -1,
    };
  },

  mounted() {
    if (this.$route.name === "search") {
      this.query = this.$route.query.keyword;
    }

    this.brandSearchIndex = this.algoliaClient.initIndex(this.$env.getOrigin() + "_brands");
    this.productSearchIndex = this.algoliaClient.initIndex(this.$env.getOrigin() + "_products");

    this.$eventBus.$on("close-search", this.closeSearch);
    window.addEventListener("resize", this.setSearchResultsHeight);
  },

  methods: {
    closeSearch,
    submit,
    searchInputChange,
    searchForBrandSuggestions: _debounce(searchForBrandSuggestions, SEARCH_DEBOUNCE_DURATION),
    searchForProductSuggestions: _debounce(searchForProductSuggestions, SEARCH_DEBOUNCE_DURATION),
    getRouterLink,
    incrementSuggestionIndex,
    decrementSuggestionIndex,
    setSearchResultsHeight,
    trackSearchButtonClick,
    trackBrandClick,
    trackProductClick,
    showUnregisteredModal,
  },

  beforeDestroy() {
    this.$eventBus.$off("close-search", this.closeSearch);
    window.removeEventListener("resize", this.setSearchResultsHeight);
  },

  watch: {
    show: async function (searchVisible) {
      if (searchVisible) {
        this.query = this.$route.query.keyword;
        await this.$nextTick();
        this.$refs.searchInput.focus();
      }
    },
    query: function (query) {
      if (query) {
        this.$nextTick(() => this.setSearchResultsHeight());
      }
    },
  },

  computed: {
    isLoadingAny,
    ...mapGetters({
      user: "userState",
    }),
  },
};

function submit() {
  if (!this.query) {
    return;
  }

  if (this.suggestionIndex === -1) {
    this.$router.push(`/${RouterPath.DEFAULT_SEARCH_PRODUCT_PAGE}?keyword=${encodeURIComponent(this.query)}`);
  } else if (this.suggestionIndex < this.brandSuggestions.length) {
    this.$router.push(this.getRouterLink(this.suggestionIndex, this.brandSuggestions));
  } else {
    this.$router.push(this.getRouterLink(this.suggestionIndex - this.brandSuggestions.length, this.productSuggestions));
  }

  this.trackSearchButtonClick();
  this.closeSearch();
}

function closeSearch() {
  this.query = "";
  this.productSuggestions = null;
  this.brandSuggestions = null;
  this.$parent.isSearchVisible = false;
  this.$eventBus.$emit("search-box-changed", false);
}

function searchInputChange(event) {
  this.query = event.target.value;
  this.$set(this.isLoading, "brands", true);
  this.$set(this.isLoading, "products", true);

  if (this.query) {
    this.searchForBrandSuggestions();
    this.searchForProductSuggestions();
  } else {
    this.brandSuggestions = null;
    this.productSuggestions = null;
  }

  this.suggestionIndex = -1;
}

function searchForBrandSuggestions() {
  this.brandSearchIndex.clearCache();
  this.brandSearchIndex
    .search(this.query, {
      page: 0,
      hitsPerPage: 7,
      minProximity: 1,
      facets: "*",
      facetFilters: [["status:APPROVED", "status:REGISTERED"], ["is_published:true"]],
    })
    .then((response) => {
      this.brandSuggestions = response.hits;
    })
    .catch((error) => {
      if (error.response) {
        this.$swal("Attention", error.response.data.error_message);
      } else {
        this.$bugsnagClient.notify(new Error(error));
      }
    })
    .finally(() => {
      this.$set(this.isLoading, "brands", false);
    });
}

function searchForProductSuggestions() {
  this.productSearchIndex.clearCache();
  this.productSearchIndex
    .search(this.query, {
      page: 0,
      hitsPerPage: 3,
      facets: "*",
      facetFilters: [...this.$utils.constants.ALGOLIA_DEFAULT_PRODUCT_SEARCH_FACET_FILTER],
      numericFilters: this.$utils.constants.ALGOLIA_DEFAULT_PRODUCT_SEARCH_NUMERIC_FILTER,
    })
    .then((response) => {
      this.productSuggestions = response.hits;
    })
    .catch((error) => {
      if (error.response) {
        this.$swal("Attention", error.response.data.error_message);
      } else {
        this.$bugsnagClient.notify(new Error(error));
      }
    })
    .finally(() => {
      this.$set(this.isLoading, "products", false);
    });
}

function incrementSuggestionIndex() {
  if (this.isLoading.brands || this.isLoading.products) {
    return;
  }

  this.suggestionIndex++;
  if (this.suggestionIndex > this.brandSuggestions.length + this.productSuggestions.length - 1) {
    this.suggestionIndex = 0;
  }
}

function decrementSuggestionIndex() {
  if (this.isLoading.brands || this.isLoading.products) {
    return;
  }

  this.suggestionIndex--;
  if (this.suggestionIndex < 0) {
    this.suggestionIndex = this.brandSuggestions.length + this.productSuggestions.length - 1;
  }
}

function getRouterLink(index, collection) {
  if (Object.is(collection, this.brandSuggestions)) {
    return {
      name: "brand-bio",
      params: {
        brandUrl: collection[index].vanity_url_name,
      },
    };
  } else if (Object.is(collection, this.productSuggestions)) {
    return {
      name: "productDetails",
      params: {
        productName: this.urlFormatter(collection[index].name),
        productId: collection[index].id,
      },
    };
  }
}

function setSearchResultsHeight() {
  if (this.$refs.searchSuggestionsEl) {
    this.$refs.searchSuggestionsEl.style.maxHeight =
      window.innerHeight - this.$refs.searchSuggestionsEl.getBoundingClientRect().top + "px";
  }
}

function isLoadingAny() {
  return this.isLoading.brands || this.isLoading.products;
}

function showUnregisteredModal(product) {
  this.$eventBus.$emit("apply-modal-unregistered", product);
}

// Tracking Events
function trackSearchButtonClick() {
  this.$ma.trackEvent({
    action: "Search dropdown button click",
    properties: {
      "Search Query": this.query,
    },
  });
}

function trackBrandClick(brand) {
  this.$ma.trackEvent({
    action: "Select Brand Search Suggestion",
    properties: {
      "Brand Name": brand.brand_name,
      "Brand Id": brand.id,
      "Search Query": this.query,
    },
  });
}
function trackProductClick(product) {
  if (!this.user) {
    this.showUnregisteredModal(product);

    return;
  }

  this.$ma.trackEvent({
    action: "Select Product Search Suggestion",
    properties: {
      "Product Name": product.name,
      "Product ID": product.id,
      "Brand Name": product.brand.company_name,
      "Brand ID": product.brand.id,
      "Search Query": this.query,
    },
  });
}
